import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { uuidv7 } from '@pos/core';
import type { RegisterRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { useServerUserId } from '../../lib/server-session.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { Alert, Badge, Button, Card, Input, PageHeader, errorMessage } from '../../ui/index.tsx';

const COLUMNS = 'id, business_id, name, is_default, archived_at, created_at, updated_at';

/**
 * Cajas del negocio. Cada caja tiene su turno (apertura y cierre) y la pueden usar varios
 * dispositivos a la vez. Se crean y cambian con conexión, directo en el servidor.
 */
export function RegistersPage() {
  const { business, writer } = useSession();
  const online = useOnline();
  const serverUser = useServerUserId();
  const canEdit = online && serverUser === writer.user_id;
  const registers = useLiveQuery(() => db.registers.where('business_id').equals(business.id).toArray(), [business.id]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const sorted = [...(registers ?? [])].sort((a, b) => Number(b.is_default) - Number(a.is_default) || a.name.localeCompare(b.name, 'es'));
  const active = sorted.filter((r) => !r.archived_at);
  const archived = sorted.filter((r) => r.archived_at);

  const create = async () => {
    if (!name.trim()) return setError('Escribí un nombre para la caja.');
    setBusy(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('registers')
        .insert({ id: uuidv7(), business_id: business.id, name: name.trim() })
        .select(COLUMNS)
        .single();
      if (dbError) throw new Error(dbError.message);
      await db.registers.put(data as RegisterRow);
      setName('');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cajas"
        description="Cada caja se abre y se cierra una vez por turno, y la pueden usar varios dispositivos a la vez. Si tenés más de una, cada dispositivo elige cuál usa desde la pantalla Caja."
      />
      {!canEdit && <Alert tone="info">Para crear o cambiar cajas hace falta conexión.</Alert>}

      <Card flush className="divide-y divide-slate-100">
        {active.map((r) => (
          <RegisterItem key={r.id} register={r} canEdit={canEdit} />
        ))}
        {registers && active.length === 0 && <p className="p-6 text-center text-sm text-slate-500">Todavía no hay cajas.</p>}
      </Card>

      <Card className="space-y-2">
        <h2 className="font-semibold">Nueva caja</h2>
        <div className="flex gap-2">
          <Input value={name} maxLength={60} placeholder="Ej.: Caja 2, Mostrador, Depósito" onChange={(e) => setName(e.target.value)} disabled={!canEdit} />
          <Button loading={busy} disabled={!canEdit} onClick={() => void create()}>
            Crear
          </Button>
        </div>
        <p className="text-xs text-slate-500">La cantidad de cajas depende de tu plan.</p>
        {error && <Alert tone="error">{error}</Alert>}
      </Card>

      {archived.length > 0 && (
        <div className="space-y-2">
          <Button variant="ghost" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Ocultar cajas eliminadas' : `Ver cajas eliminadas (${archived.length})`}
          </Button>
          {showArchived && (
            <Card flush className="divide-y divide-slate-100">
              {archived.map((r) => (
                <RegisterItem key={r.id} register={r} canEdit={canEdit} />
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function RegisterItem({ register, canEdit }: { register: RegisterRow; canEdit: boolean }) {
  const [name, setName] = useState(register.name);
  const [renaming, setRenaming] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isOpen = useLiveQuery(
    () => db.cash_sessions.where('register_id').equals(register.id).filter((s) => s.closed_at === null && !s.device_only).count(),
    [register.id],
  );

  const update = async (changes: { name?: string; archived_at?: string | null }): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase.from('registers').update(changes).eq('id', register.id).select(COLUMNS).single();
      if (dbError) throw new Error(dbError.message);
      await db.registers.put(data as RegisterRow);
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex gap-2">
              <Input value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} />
              <Button
                loading={busy}
                onClick={async () => {
                  if (name.trim() && name.trim() !== register.name && !(await update({ name: name.trim() }))) return;
                  setRenaming(false);
                }}
              >
                OK
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setName(register.name);
                  setRenaming(false);
                }}
              >
                Cancelar
              </Button>
            </div>
          ) : (
            <p className="truncate font-medium">{register.name}</p>
          )}
        </div>
        {register.is_default && <Badge tone="brand">Principal</Badge>}
        {!!isOpen && <Badge tone="green">Abierta</Badge>}
        {!renaming && canEdit && (
          <div className="flex gap-2">
            {register.archived_at ? (
              <Button variant="ghost" loading={busy} onClick={() => void update({ archived_at: null })}>
                Recuperar
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setRenaming(true)}>
                  Renombrar
                </Button>
                {!register.is_default && (
                  <Button variant="dangerGhost" onClick={() => setConfirmArchive(true)}>
                    Eliminar
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {confirmArchive && (
        <Alert tone="warning" title={`¿Eliminar "${register.name}"?`}>
          <p>Ya no se va a poder abrir. Su historial de turnos se conserva y la podés recuperar después.</p>
          <div className="mt-3 flex gap-2">
            <Button variant="secondary" onClick={() => setConfirmArchive(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={async () => {
                if (await update({ archived_at: new Date().toISOString() })) setConfirmArchive(false);
              }}
            >
              Eliminar
            </Button>
          </div>
        </Alert>
      )}
      {error && <Alert tone="error">{error}</Alert>}
    </div>
  );
}
